import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Search,
  Cog,
  Car,
  Lightbulb,
  Disc,
  Armchair,
  GitFork,
  ArrowRight,
  Loader2,
  Package,
  Thermometer,
  Droplets,
  Zap,
  Fuel,
} from "lucide-react"; // Added icons
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils"; // Import cn utility
import { useDispatch, useSelector } from "react-redux";
import { fetchLandingPageCategories } from "../../store/categories/categoriesAction";

const CategoriesSection = ({ variants }) => {
  const dispatch = useDispatch();
  const { landingPageCategories, landingPageCategoriesLoading } = useSelector(
    (state) => state.categories
  );
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  // Map category names to icons
  const categoryIcons = {
    Transmission: Cog,
    Engine: Cog,
    Steering: GitFork,
    Brakes: Disc,
    "Body Panel": Car,
    "Wheels & Tires": Disc,
    Exhaust: Package,
    "Cooling & Heating": Thermometer,
    "Filters & Fluids": Droplets,
    Suspension: GitFork,
    Electrical: Zap,
    Lighting: Lightbulb,
    Interior: Armchair,
    "Fuel System": Fuel,
    Others: Package,
  };

  useEffect(() => {
    dispatch(fetchLandingPageCategories());
  }, []);

  if (landingPageCategoriesLoading) {
    return (
      <motion.section className="py-12 bg-secondary/30" variants={variants}>
        <div className="container mx-auto px-4 text-center flex flex-col items-center justify-center min-h-[300px]">
          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-lg text-muted-foreground">Loading categories...</p>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section className="py-14 sm:py-16" variants={variants}>
      <motion.h2
        className="text-2xl sm:text-3xl font-bold text-center mb-10 sm:mb-12 text-foreground"
        variants={itemVariants}
      >
        Browse parts by category {/* Updated Title */}
      </motion.h2>
      {landingPageCategories.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 container mx-auto px-4">
          {landingPageCategories.map((category) => {
            const Icon = categoryIcons[category.name] || Package;
            return (
              <motion.div key={category.id} variants={itemVariants}>
                <Link
                  to={`/listings/category/${category.id}`}
                  className="block group h-full"
                >
                  {" "}
                  {/* Make the whole card a link */}
                  <Card
                    className={cn(
                      "h-full flex flex-col overflow-hidden group transition-all duration-300 ease-in-out border",
                      "hover:shadow-lg hover:border-primary/50 hover:-translate-y-1",
                      "bg-card hover:bg-accent/50" // Subtle background change on hover
                    )}
                  >
                    <CardContent className="p-4 md:p-6 flex flex-col items-center text-center">
                      <Icon className="h-8 w-8 md:h-10 md:w-10 mb-3 text-primary transition-colors duration-300 group-hover:text-accent-foreground" />
                      <h3 className="text-sm md:text-base font-medium text-foreground transition-colors duration-300 group-hover:text-accent-foreground">
                        {category.name}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <motion.p
          className="text-center text-muted-foreground py-10"
          variants={itemVariants}
        >
          No categories listed yet.
        </motion.p>
      )}
      <motion.div className="text-center mt-12" variants={itemVariants}>
        <Link to="/listings">
          <Button
            size="lg"
            variant="ghost"
            className="text-primary text-base sm:text-lg group hover:bg-primary/10"
          >
            See All Listings
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </motion.div>
    </motion.section>
  );
};

export default CategoriesSection;
